import React, { useMemo } from "react"
import { Row, Col, Dropdown, Space, Flex, Button } from "antd"
import {
  EnvironmentOutlined,
  UserOutlined,
  ClockCircleOutlined,
  MoreOutlined
} from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { Paragraph, Text, Title } from "ui/Typography"
import { Tag } from "ui/Tag"
import { Icon } from "ui/Icon"
import { EXPERIENCE_STATES } from "pagesV2/pro/Experiences/Home/constants"
import { formatDuration, getActionConditions, getDropdownItems } from "./utils"
import { getMenuConfig } from "./constants"
import styles from "./ExperienceCard.module.css"

export const ExperienceCard = ({ 
  trip, 
  onEdit, 
  onPreview, 
  onShare, 
  onDuplicate, 
  onDeactivate, 
  onDelete, 
  onPublish 
}) => {
  const { t } = useTranslation()
  const {
    title,
    status,
    flat_rate,
    price_per_person,
    address,
    max_guests,
    trip_length,
    available_hours,
    photos,
    publish_on_guides_site,
    publish_on_platform
  } = trip

  const price = flat_rate || price_per_person || 0
  const imageUrl = photos && photos.length > 0 ? photos[0].preview_url : "https://fakeimg.pl/200x200"
  const published = status === EXPERIENCE_STATES.published

  const menuConfig = useMemo(() => getMenuConfig(t, {
    onShare,
    onDuplicate,
    onDeactivate,
    onDelete,
    onPublish
  }), [t, onShare, onDuplicate, onDeactivate, onDelete, onPublish])

  const conditions = useMemo(() => getActionConditions(status), [status])
  const dropdownItems = useMemo(() => getDropdownItems(menuConfig, conditions), [menuConfig, conditions])

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.imgWrapper}>
        <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
      </div>

      <div className={styles.content}>
        <Row justify="space-between" align="top">
          <Col>
            <Title level={5} style={{ margin: 0, fontWeight: 500 }}>
              {title}
            </Title>
          </Col>
          <Col>
            <Flex gap={8}>
              <Text className="color-text-secondary">from</Text>
              <Text className="text-color-primary" strong>
                ${parseFloat(price).toFixed(2)}
              </Text>
            </Flex>
          </Col>
        </Row>
        <Row justify="space-between" align="top">
          <Col span={12}>
            <Space align="top">
              <EnvironmentOutlined className="color-text-secondary-dark" />
              <Paragraph ellipsis={{ rows: 2 }} className="mb-0 color-text-secondary-dark">
                {address}
              </Paragraph>
            </Space>
          </Col>
          <Col>
            <Space size={16}>
              <Space>
                <UserOutlined />
                <Text className="lowercased color-text-secondary-dark">
                  {max_guests} {t("global.max")}
                </Text>
              </Space>
              <Space>
                <ClockCircleOutlined />
                <Text className="color-text-secondary-dark">
                  {formatDuration(trip_length, available_hours)}
                </Text>
              </Space>
            </Space>
          </Col>
        </Row>

        {published && (
          <Row>
            <Flex align="center">
              <Text variant="caption" className="mr-xs">
                {t("experiences.empty_state.published")}
              </Text>
              {publish_on_platform && (
                <Tag type="success">
                  <Flex gap={4} align="center">
                    <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                    {t("global.ds_marketplace")}
                  </Flex>
                </Tag>
              )}
              {publish_on_guides_site && (
                <Tag type="success">
                  <Flex gap={4} align="center">
                    <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                    {t("global.guides_website")}
                  </Flex>
                </Tag>
              )}
            </Flex>
          </Row>
        )}

        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button color="primary" variant="outlined" onClick={() => onEdit?.(trip)}>
                Edit
              </Button>
              <Button color="primary" variant="outlined" onClick={() => onPreview?.(trip)}>
                Preview
              </Button>
            </Space>
          </Col>
          <Col>
            <Dropdown menu={{ items: dropdownItems }} trigger={["click"]} placement="bottomRight">
              <div className={styles.actionButtonWrapper}>
                <Button color="primary" variant="text" icon={<MoreOutlined />} />
              </div>
            </Dropdown>
          </Col>
        </Row>
      </div>
    </div>
  )
}
ExperienceCard.propTypes = {
  trip: PropTypes.shape({
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(EXPERIENCE_STATES)).isRequired,
    flat_rate: PropTypes.number,
    price_per_person: PropTypes.number,
    address: PropTypes.string.isRequired,
    max_guests: PropTypes.number.isRequired,
    trip_length: PropTypes.oneOf(['full_day', 'half_day']),
    available_hours: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired
      })
    ),
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    publish_on_guides_site: PropTypes.bool,
    publish_on_platform: PropTypes.bool
  }).isRequired,
  onEdit: PropTypes.func,
  onPreview: PropTypes.func,
  onShare: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func
}

ExperienceCard.defaultProps = {
  onEdit: () => {},
  onPreview: () => {},
  onShare: () => {},
  onDuplicate: () => {},
  onDeactivate: () => {},
  onDelete: () => {},
  onPublish: () => {}
}
